import { useEffect, useRef, useState } from 'react';
import confirmationGif from '../assets/animations/buyer-confirmation.gif';

const DURATION_MS = 1370;
const GIF_WIDTH = 800;
const GIF_HEIGHT = 600;

const BuyerConfirmationAnimation = ({ onComplete, holdMs = 700 }) => {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [frozen, setFrozen] = useState(false);
  const [src] = useState(() => `${confirmationGif}?t=${Date.now()}`);

  useEffect(() => {
    const imgEl = imgRef.current;
    if (!imgEl) { return undefined; }

    let freezeTimer;
    let completeTimer;

    const freeze = () => {
      const canvasEl = canvasRef.current;
      if (canvasEl && imgEl.naturalWidth > 0) {
        try {
          const ctx = canvasEl.getContext('2d');
          if (ctx) {
            ctx.drawImage(imgEl, 0, 0, canvasEl.width, canvasEl.height);
            setFrozen(true);
          }
        } catch {
          // Snapshot failed — keep the GIF visible; it will still be at/near the last frame.
        }
      }
      if (onComplete) {
        completeTimer = setTimeout(onComplete, holdMs);
      }
    };

    const start = () => {
      freezeTimer = setTimeout(freeze, DURATION_MS - 30);
    };

    if (imgEl.complete && imgEl.naturalWidth > 0) {
      start();
    } else {
      imgEl.addEventListener('load', start, { once: true });
    }

    return () => {
      clearTimeout(freezeTimer);
      clearTimeout(completeTimer);
      imgEl.removeEventListener('load', start);
    };
  }, [onComplete, holdMs]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Order confirmed"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/95 backdrop-blur-sm"
    >
      <div className="relative w-[min(80vw,600px)] aspect-[4/3]">
        <img
          ref={imgRef}
          src={src}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-contain ${frozen ? 'invisible' : ''}`}
        />
        <canvas
          ref={canvasRef}
          width={GIF_WIDTH}
          height={GIF_HEIGHT}
          className={`absolute inset-0 w-full h-full object-contain ${frozen ? '' : 'invisible'}`}
        />
      </div>
    </div>
  );
};

export default BuyerConfirmationAnimation;
